import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import { SIcon } from '@chargebee/sting-react';
import {
  PivotTableComponent,
  type PivotT,
} from '@bbkAdminComponents/pivot-table/pivot-table';
import { PivotTableTooltipContent } from '@bbkAdminComponents/pivot-table/pivot-table-content-tooltip';
import { resolvePerfReportGroupBy } from '@bbkAdminComponents/pivot-table/resolve-perf-report-group-by';
import { useShowPlayDemoData } from '@bbkAdminComponents/play/play-context';
import {
  createExperiencePerformanceCalculatedFields,
  DEFAULT_EXPERIENCE_PERF_GROUP_BY,
  EXPERIENCE_PERF_TABLE_NAME,
  filterExperienceTrendsRows,
  getExperiencePerformancePivotRowDefinitions,
  getExperiencePerformancePivotValues,
  getExperiencePerformanceVisibleColumns,
  resolveExperiencePerformancePivotRows,
  type ExperiencePerformanceRow,
} from '@bbkAdminComponents/unified-plays/reporting/reports/cancel-page/cancel-page-experience-performance-export.config';
import { type CancelPlayReportProps } from '@bbkAdminComponents/unified-plays/reporting/reports/cancel-page/cancel-page-reports';
import {
  selectAppId,
  selectCompanyInternalName,
} from '@bbkAdminRedux/app/selectors';
import {
  selectFromResultTyped,
  toDictionary,
  useAppDispatch,
} from '@bbkAdminRedux/redux_utils';
import {
  appPersistSlice,
  selectExpPerfReportNav,
} from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { billingConfigSlice } from '@bbkAdminRedux/rtkq/billing-config.slice';
import {
  DEFAULT_BRAND_ID,
  useGetBrandOptions,
} from '@bbkAdminRedux/rtkq/brands.slice';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import {
  useSelectAllFunnelsEntities,
  useSelectFunnelsLoading,
} from '@bbkAdminRedux/rtkq/funnels.slice';
import { useSelectBillingIntegration } from '@bbkAdminRedux/rtkq/integrations.slice';
import {
  reportsSlice,
  ReportType,
  type GetReportV2Args,
  type TrendsReport,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import {
  rulesSlice,
  RuleType,
  useSelectAllRulesEntities,
} from '@bbkAdminRedux/rtkq/rules.slice';
import {
  surveyReasonsSlice,
  useSelectAllVanityReasons,
} from '@bbkAdminRedux/rtkq/survey-reasons.slice';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { NumberKit } from '@bbkAdminUtils/number-kit';
import { usePlans } from '@bbkAdminUtils/plans';
import { useStrings } from '@bbkAdminUtils/strings';

const PlayExpPerf: React.FC<CancelPlayReportProps> = ({ play }) => {
  const dispatch = useAppDispatch();
  const companyInternalName = useSelector(selectCompanyInternalName);
  const appKey = useSelector(selectAppId);
  const brandOptions = useGetBrandOptions({ appKey });
  const strings = useStrings();
  const brandId = play.brand_id ?? DEFAULT_BRAND_ID;

  const selectedBrand = useMemo(() => {
    return brandOptions.find((b) => b.value === brandId);
  }, [brandOptions, brandId]);

  const isDemoData = useShowPlayDemoData();

  const isNestedAudiencesEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_NESTED_AUDIENCES_IN_PERF_REPORTS
  );

  const { isStarterFreePlan } = usePlans();
  const exportBtnTooltip = {
    content: strings.UPGRADE_PLAN({
      description: strings.UPGRADE_TO_STARTER_PREMIUM_EXPORT,
    }),
    tooltipOptions: {
      placement: 'left',
    },
  };

  const {
    groupBy: initGroupByOpts,
    visibleColumns: initVisibleColumns,
    sortField: initSortField,
    sortOrderDesc: initSortOrderDesc,
  } = useSelector(selectExpPerfReportNav(appKey));

  const billingIntegration = useSelectBillingIntegration({ appKey });

  const { data: billingConfig } =
    billingConfigSlice.endpoints.getBillingConfig.useQuery(
      billingIntegration ? { app_key: appKey } : skipToken
    );
  const isRepurchaseAttributionEnabled =
    billingConfig?.repurchase_based_attribution;

  const { startDate: start, endDate: end } = useSelectDatePicker();
  const request = {
    report_type: ReportType.trends,
    parameters: {
      group_by_period: 'all',
      group_by: 'funnel_id,audiences,vanity_reason',
    },
  };

  const args: GetReportV2Args = {
    appKey,
    context: isDemoData ? BBKRequestContext.reports_filter : undefined,
    start,
    end,
    report_type_requests: [request],
    filters: [[{ field: 'play', operator: 'equals', value: play.id }]],
  };
  const opts = {
    skip: !appKey || !start || !end,
    selectFromResult: selectFromResultTyped<TrendsReport>,
  };
  const { data: trendsReport, isFetching: isTrendsFetching } =
    reportsSlice.endpoints.getReportV2.useQuery(args, opts);

  const data = filterExperienceTrendsRows(trendsReport?.trends ?? []);

  const rulesArgs = {
    appKey,
    context: BBKRequestContext.exp_perf,
    includeAdditionalRules: [RuleType.SPLIT],
    brandId,
  };
  const { isFetching: isRulesFetching } =
    rulesSlice.endpoints.getRules.useQuery(rulesArgs);
  const rulesMap = useSelectAllRulesEntities(rulesArgs);

  const reasonsArgs = {
    companyInternalName,
    appKey,
    context: BBKRequestContext.exp_perf,
    brandId,
  };
  const vanityReasons = useSelectAllVanityReasons(reasonsArgs);
  const { isFetching: isReasonsFetching } =
    surveyReasonsSlice.endpoints.getReasons.useQuery(reasonsArgs);
  const vanityReasonsMap = toDictionary(
    vanityReasons.map((vr) => ({ ...vr, id: vr.internal_name }))
  );

  const funnelsArgs = { appKey, context: BBKRequestContext.exp_perf, brandId };
  const { isFetching: isFunnelsFetching } =
    useSelectFunnelsLoading(funnelsArgs);

  const funnelsMap = useSelectAllFunnelsEntities(funnelsArgs);

  const isFetching =
    isTrendsFetching ||
    isRulesFetching ||
    isReasonsFetching ||
    isFunnelsFetching;

  const calculatedFields = useMemo(
    () =>
      createExperiencePerformanceCalculatedFields({
        rulesMap,
        vanityReasonsMap,
        funnelsMap,
        selectedBrandLabel:
          typeof selectedBrand?.label === 'string'
            ? selectedBrand.label
            : undefined,
      }),
    [rulesMap, vanityReasonsMap, funnelsMap, selectedBrand?.label]
  );

  type PivotRow = PivotT.ComputedFields<
    ExperiencePerformanceRow,
    typeof calculatedFields
  >;
  type PivotValues = PivotT.PivotValue<PivotRow>[];

  const pivotRows = useMemo(() => {
    const allGroupByNames = getExperiencePerformancePivotRowDefinitions(
      isNestedAudiencesEnabled
    ).map((row) => row.name);

    return resolveExperiencePerformancePivotRows(
      allGroupByNames,
      isNestedAudiencesEnabled
    );
  }, [isNestedAudiencesEnabled]);

  const pivotValues = useMemo((): PivotValues => {
    const basePivotValues = getExperiencePerformancePivotValues(
      isRepurchaseAttributionEnabled
    );

    return basePivotValues.map((pivotValue) => {
      switch (pivotValue.field) {
        case 'deflectRate':
          return {
            ...pivotValue,
            tooltip: (row) => (
              <PivotTableTooltipContent
                formula="Deflects / Total sessions"
                calculation={
                  <>
                    {row.deflects} / {row.totalSessions} ={' '}
                    {NumberKit.formatPercent(row.deflectRate)}
                  </>
                }
              />
            ),
          };
        case 'saveRate':
          return {
            ...pivotValue,
            tooltip: (row) => (
              <PivotTableTooltipContent
                formula="Saved / (Canceled + Saved)"
                calculation={
                  <>
                    {row.cohort_saved} / ({row.cohort_canceled} +{' '}
                    {row.cohort_saved}){' = '}
                    {NumberKit.formatPercent(row.saveRate)}
                  </>
                }
              />
            ),
          };
        case 'modalOfferAcceptRate':
          return {
            ...pivotValue,
            tooltip: (row) => (
              <PivotTableTooltipContent
                formula="Modal offer accepts / Modal offer views"
                calculation={
                  <>
                    {row.modal_offer_accepts} / {row.modal_offer_views} ={' '}
                    {NumberKit.formatPercent(row.modalOfferAcceptRate)}
                  </>
                }
              />
            ),
          };
        case 'laOfferAcceptsRate':
          return {
            ...pivotValue,
            tooltip: (row) => (
              <PivotTableTooltipContent
                formula="LA offer accepts / LA offer views"
                calculation={
                  <>
                    {row.la_offer_accepts} / {row.la_offer_views} ={' '}
                    {NumberKit.formatPercent(row.laOfferAcceptsRate)}
                  </>
                }
              />
            ),
          };
        case 'passiveDeflectRate':
          return {
            ...pivotValue,
            tooltip: (row) => (
              <PivotTableTooltipContent
                formula="Passive deflects / Total sessions"
                calculation={
                  <>
                    {row.passiveDeflects} / {row.totalSessions} ={' '}
                    {NumberKit.formatPercent(row.passiveDeflectRate)}
                  </>
                }
              />
            ),
          };
        case 'firstTimeRepurchaseRate':
          return {
            ...pivotValue,
            tooltip: (row) => (
              <PivotTableTooltipContent
                formula="First Repurchase Order / Deflects"
                calculation={
                  <>
                    {row.deflect_payment_sessions} / {row.deflects} ={' '}
                    {NumberKit.formatPercent(row.firstTimeRepurchaseRate)}
                  </>
                }
              />
            ),
          };
        case 'winbackRate':
          return {
            ...pivotValue,
            tooltip: (row) => (
              <PivotTableTooltipContent
                formula="First Winback Orders / (Canceled + Lost Deflects)"
                calculation={
                  <>
                    {row.cancel_payment_sessions} / ({row.direct_canceled} +{' '}
                    {row.lostDeflects}) ={' '}
                    {NumberKit.formatPercent(row.winbackRate)}
                  </>
                }
              />
            ),
          };
        case 'activeDeflectRate':
          return {
            ...pivotValue,
            tooltip: (row) => (
              <PivotTableTooltipContent
                formula="Active deflects / Total deflects"
                calculation={
                  <>
                    {row.deflect_still_paying} / {row.deflects} ={' '}
                    {row.deflects === 0
                      ? '--'
                      : NumberKit.formatPercent(row.activeDeflectRate)}
                  </>
                }
              />
            ),
          };
        default:
          return pivotValue;
      }
    });
  }, [isRepurchaseAttributionEnabled]);

  return (
    <div>
      <div className="tw-mb-3 tw-mt-2 tw-flex tw-items-center tw-gap-2">
        <div className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-primary-100 tw-bg-primary-50 tw-p-2">
          <SIcon name="circle-x" size={18} className="tw-text-primary-600" />
        </div>
        <span className="tw-text-lg tw-font-semibold tw-text-neutral-900">
          Experience performance report
        </span>
      </div>
      <div className="tw-text-sm tw-font-light tw-text-neutral-600">
        Select an audience to show a breakdown by experience (cancel page).
      </div>
      <div className="tw-mb-6">
        <PivotTableComponent
          tableName={EXPERIENCE_PERF_TABLE_NAME}
          isFetching={isFetching}
          isExportDisabled={isStarterFreePlan || isDemoData}
          exportTooltip={isStarterFreePlan ? exportBtnTooltip : undefined}
          data={data}
          calculatedFields={calculatedFields}
          pivotValues={pivotValues}
          pivotRows={pivotRows}
          visibleColumns={
            initVisibleColumns ?? getExperiencePerformanceVisibleColumns()
          }
          groupByOpts={resolvePerfReportGroupBy(
            initGroupByOpts,
            pivotRows.map((row) => row.name),
            DEFAULT_EXPERIENCE_PERF_GROUP_BY
          )}
          onVisibleColumnsChange={(value) => {
            dispatch(
              appPersistSlice.actions.saveExpPerfReportNavState({
                appKey,
                visibleColumns: value,
              })
            );
          }}
          onGroupByOptsChange={(value) => {
            dispatch(
              appPersistSlice.actions.saveExpPerfReportNavState({
                appKey,
                groupBy: value.filter(isDefined),
              })
            );
          }}
          sortField={initSortField}
          sortOrderDesc={initSortOrderDesc}
          onSortChange={(sortField, sortOrderDesc) => {
            dispatch(
              appPersistSlice.actions.saveExpPerfReportNavState({
                appKey,
                sortField,
                sortOrderDesc,
              })
            );
          }}
        />
      </div>
    </div>
  );
};

export default PlayExpPerf;
